/** @jsxImportSource @emotion/react */
import {
  EuiFieldSearch,
  EuiFlexGroup,
  EuiFlexItem,
  EuiSuperSelect,
  EuiSuperSelectProps,
  htmlIdGenerator,
} from "@inscopix/ideas-eui";
import { FileBrowserFile } from "components/FileBrowser/FileBrowser";
import { useEffect, useState } from "react";

import assert from "assert";

type FileBrowserFileSources = "UPLOADED" | "ANALYSIS_RESULTS" | "ALL";

type Filter<
  T extends string,
  U extends FileBrowserFile,
> = EuiSuperSelectProps<T>["options"][number] & {
  onFilter: (file: U) => boolean;
};

export type FileBrowserFilterOptions<T extends FileBrowserFile> = {
  source: FileBrowserFileSources;
  datasetFilter: DatasetFilter<T>[number] | undefined;
};

export type DatasetFilter<T extends FileBrowserFile> = (Filter<string, T> & {
  default?: boolean;
})[];

export interface FileBrowserSearchAndFilterProps<T extends FileBrowserFile> {
  selectedOptions: FileBrowserFilterOptions<T>;
  onChangeSelectedOptions: React.Dispatch<
    React.SetStateAction<FileBrowserFilterOptions<T>>
  >;
  query: string;
  onChangeQuery: (query: string) => void;
  datasetFilter?: DatasetFilter<T>;
}
export const FileBrowserSearchAndFilter = <T extends FileBrowserFile>({
  selectedOptions,
  onChangeSelectedOptions,
  query,
  onChangeQuery,
  datasetFilter,
}: FileBrowserSearchAndFilterProps<T>) => {
  const selectFileSourceOptions: EuiSuperSelectProps<FileBrowserFileSources>["options"] =
    [
      { value: "ALL", inputDisplay: "All Files" },
      { value: "UPLOADED", inputDisplay: "Uploaded Files" },
      { value: "ANALYSIS_RESULTS", inputDisplay: "Analysis Results" },
    ];

  const [value, setValue] = useState(query);
  const fileSourceId = htmlIdGenerator("file_source_select")();
  const datasetFilterId = htmlIdGenerator("file_dataset_status")();

  // if the user clears the search bar by erasing all characters, reset search
  useEffect(() => {
    if (value === "" && query !== "") {
      onChangeQuery("");
    }
  }, [onChangeQuery, query, value]);

  return (
    <EuiFlexGroup justifyContent="flexStart" responsive={false}>
      <EuiFlexItem grow={true} css={{ minWidth: 200, maxWidth: 500 }}>
        <EuiFieldSearch
          compressed
          fullWidth
          placeholder="Search..."
          value={value}
          onChange={(e) => setValue(e.target.value)}
          onSearch={(query) => onChangeQuery(query)}
          incremental={false}
        />
      </EuiFlexItem>
      {datasetFilter !== undefined && (
        <EuiFlexItem grow={false} css={{ width: 200 }}>
          <EuiSuperSelect
            id={datasetFilterId}
            compressed
            options={datasetFilter}
            aria-label="Select dataset"
            onChange={(newOption) =>
              onChangeSelectedOptions((prev) => ({
                ...prev,
                datasetFilter: (() => {
                  const option = datasetFilter.find(
                    (option) => option.value === newOption,
                  );
                  assert(option !== undefined);
                  return option;
                })(),
              }))
            }
            valueOfSelected={selectedOptions.datasetFilter?.value}
          />
        </EuiFlexItem>
      )}

      <EuiFlexItem grow={false} css={{ width: 200 }}>
        <EuiSuperSelect
          id={fileSourceId}
          compressed
          aria-label="Select file sources"
          valueOfSelected={selectedOptions.source}
          onChange={(newOption) =>
            onChangeSelectedOptions((prev) => ({
              ...prev,
              source: newOption,
            }))
          }
          options={selectFileSourceOptions}
        />
      </EuiFlexItem>
    </EuiFlexGroup>
  );
};
